import React, { useState, useMemo, useEffect, useRef } from "react";
import CountryCodes from "../../Utils/CountryCodes";
import { Input, Button, Box, Text, useBreakpointValue } from "@chakra-ui/react";
import ReactCountryFlag from "react-country-flag";
import { useThemeContext } from "../../Context/ThemeContext";
import { useTheme } from "next-themes";

export const CountrySelector = ({ onChange }) => {
  const { themeConfig } = useThemeContext();
  const { resolvedTheme } = useTheme();
  const theme = themeConfig[resolvedTheme === 'dark' ? 'dark' : 'light'];

  // Find the default UAE country (fix the code to "AE" for proper flag display)
  const defaultCountry = CountryCodes.find(country => country.code === "AE") || 
                        CountryCodes.find(country => country.code === "UAE") || 
                        CountryCodes.find(country => country.dial_code === "+971") ||
                        CountryCodes[0];
                        
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false });
  const dropdownMaxHeight = useBreakpointValue({ base: "200px", md: "300px" });
  const dropdownMinWidth = useBreakpointValue({ base: "150px", md: "200px" });

  // Filter countries based on search term
  const filteredCountries = useMemo(() => {
    if (!searchTerm) return CountryCodes;
    return CountryCodes.filter(country => 
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.dial_code.includes(searchTerm)
    );
  }, [searchTerm]);

  // IP Geolocation service for accurate country detection
  const getCountryFromIP = async () => {
    try {
      // Try first IP geolocation service
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
       
        
        if (data && data.country_code) {
          // Special handling for UAE - some services might return different codes
          let countryCode = data.country_code;
          if (countryCode === "UAE") {
            countryCode = "AE";
          }
          
          // Try to find country by country code
          const detectedCountry = CountryCodes.find(country => 
            country.code === countryCode || 
            (country.code === "UAE" && countryCode === "AE")
          );
          
          if (detectedCountry) {
          
            return detectedCountry;
          }
        }
      } catch (error) {
        console.log("First IP geolocation service failed:", error);
      }
      
      
      // Fallback to second IP geolocation service
      try {
        const response = await fetch('https://ipinfo.io/json');
        const data = await response.json();
        
   
        
        if (data && data.country) {
          // Special handling for UAE
          let countryCode = data.country;
          if (countryCode === "UAE") {
            countryCode = "AE";
          }
          
          // Try to find country by country code
          const detectedCountry = CountryCodes.find(country => 
            country.code === countryCode || 
            (country.code === "UAE" && countryCode === "AE")
          );
          
          if (detectedCountry) {
           
            return detectedCountry;
          }
        }
      } catch (error) {
        console.log("Second IP geolocation service failed:", error);
      }
      
      return null;
    } catch (error) {
      console.log("IP geolocation failed:", error);
      return null;
    }
  };

  // Enhanced country detection with multiple fallback methods
  const detectUserCountry = async () => {
    try {
   
      
      // Method 1: Try IP geolocation (most accurate)
      const ipCountry = await getCountryFromIP();
      if (ipCountry) {
       
        
        return ipCountry;
      }
      
      // Method 2: Try to detect based on browser language
      const userLang = navigator.language || navigator.userLanguage;
    
      if (userLang && userLang.includes('-')) {
        const countryCode = userLang.split('-')[1].toUpperCase();
       
        
        // Special handling for UAE
        let searchCode = countryCode;
        if (countryCode === "UAE") {
          searchCode = "AE";
        }
        
        // First try to find by standard 2-letter code
        let detectedCountry = CountryCodes.find(country => 
          country.code === searchCode || 
          (country.code === "UAE" && searchCode === "AE")
        );
        
    
        // If found, select it
        if (detectedCountry) {
          return detectedCountry;
        }
      }
      
      // Method 3: Try to detect based on timezone offset
      const timezoneOffset = new Date().getTimezoneOffset();
     
      // Map timezone offsets to countries
      const timezoneToCountry = {
        '-240': 'AE', // UAE is UTC+4
        '0': 'GB',    // UK is UTC+0
        '-300': 'PK', // Pakistan is UTC+5
        '-330': 'IN', // India is UTC+5:30
        '-480': 'CN', // China is UTC+8
        // Add more timezone mappings as needed
      };
      
      const countryFromTimezone = timezoneToCountry[timezoneOffset.toString()];
      if (countryFromTimezone) {
        const detectedCountry = CountryCodes.find(country => 
          country.code === countryFromTimezone || 
          (country.code === "UAE" && countryFromTimezone === "AE")
        );
        if (detectedCountry) {
          
          return detectedCountry;
        }
      }
      
      // Method 4: Try to detect based on Intl API (if available)
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
        if (timezone) {
          // Map timezones to countries
          const timezoneMapping = {
            'Asia/Dubai': 'AE',
            'Europe/London': 'GB',
            'Asia/Kolkata': 'IN',
            'Asia/Shanghai': 'CN',
            'Asia/Karachi': 'PK',
            // Add more mappings as needed
          };
          
          const countryCode = timezoneMapping[timezone];
          if (countryCode) {
            const detectedCountry = CountryCodes.find(country => 
              country.code === countryCode || 
              (country.code === "UAE" && countryCode === "AE")
            );
            if (detectedCountry) {
           
              return detectedCountry;
            }
          }
        }
      } catch (error) {
        console.log("Intl API detection failed");
      }
      
      // Special check for UAE users who might be getting UK by mistake
      // If timezone is UTC+4 (-240) but we're getting UK, force UAE
      const currentTimezoneOffset = new Date().getTimezoneOffset();
      if (currentTimezoneOffset === -240) {
        const uaeCountry = CountryCodes.find(country => 
          country.code === "AE" || country.code === "UAE"
        );
        if (uaeCountry) {
        
          return uaeCountry;
        }
      }
      
      // Fallback to default UAE
    
      return defaultCountry;
    } catch (error) {
      console.log("Country detection failed, using default UAE:", error);
      return defaultCountry;
    }
  };

  // Detect user's country on component mount
  useEffect(() => {
    const initializeCountry = async () => {
      const detectedCountry = await detectUserCountry();
    
      setSelectedCountry(detectedCountry);
      onChange(detectedCountry.dial_code);
    };

    initializeCountry();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    onChange(country.dial_code);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Ensure proper country code for flag display
  const getCountryCodeForFlag = (country) => {
    // UAE should use "AE" for proper flag display
    if (country.code === "UAE") {
      return "AE";
    }
    return country.code;
  };

  return (
    <Box ref={dropdownRef} position="relative" width="100%">
      <Button
        width="100%"
        height="40px"
        padding={2}
        borderRadius="md"
        borderWidth="1px"
        borderColor={theme.inputBorder}
        bg={theme.inputBg}
        color={theme.inputText}
        _focus={{
          borderColor: theme.inputBorderFocus,
          boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
        }}
        _hover={{ bg: theme.bgTertiary }}
        textAlign="left"
        justifyContent="space-between"
        size="sm"
        onClick={toggleDropdown}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center">
            <ReactCountryFlag
              countryCode={getCountryCodeForFlag(selectedCountry)}
              svg
              style={{
                width: '1.2em',
                height: '1.2em',
                marginRight: '8px',
              }}
              title={selectedCountry?.name}
            />
            <span>{selectedCountry?.dial_code}</span>
          </Box>
          <span>▼</span>
        </Box>
      </Button>
      
      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left="0"
          right="0"
          mt="1"
          maxHeight={dropdownMaxHeight}
          width="100%"
          minWidth={dropdownMinWidth}
          zIndex={9999}
          bg={theme.bgCard}
          borderWidth="1px"
          borderColor={theme.borderPrimary}
          borderRadius="md"
          boxShadow={theme.shadowElevated}
          overflow="hidden"
        >
          <Box p={2}>
            <Input
              placeholder="Search country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
              borderRadius="md"
              borderWidth="1px"
              borderColor={theme.inputBorder}
              bg={theme.inputBg}
              color={theme.inputText}
              _placeholder={{ color: theme.inputPlaceholder }}
              paddingLeft="12px"
            />
          </Box>
          <Box 
            maxHeight={isMobile ? "150px" : "200px"} 
            overflowY="auto" 
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: theme.bgSecondary,
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme.borderSecondary,
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: theme.textTertiary,
              },
            }}
          >
            {filteredCountries.map((country) => (
              <Box
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                py={2}
                px={3}
                _hover={{ bg: theme.bgTertiary }}
                fontSize="sm"
                cursor="pointer"
                color={theme.textPrimary}
              >
                <Box display="flex" alignItems="center">
                  <ReactCountryFlag
                    countryCode={getCountryCodeForFlag(country)}
                    svg
                    style={{
                      width: '1.2em',
                      height: '1.2em',
                      marginRight: '8px',
                    }}
                    title={country.name}
                  />
                  <Text 
                    whiteSpace={isMobile ? "nowrap" : "normal"}
                    overflow={isMobile ? "hidden" : "visible"}
                    textOverflow={isMobile ? "ellipsis" : "clip"}
                  >
                    {country.name} ({country.dial_code})
                  </Text>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};